<template>
  <header :class="b('', { isMobileMenuActive })">
    <nav :class="b('container')">
      <div :class="b('brand-logo-container')">
        <a
          :class="b('brand-link')"
          :href="sessionStore.logoLink"
          rel="noopener noreferrer"
        >
          <img
            ref="logo"
            :src="sessionStore.logoPath"
            :class="b('brand-logo')"
            :alt="$t('c-header.logo-alt')"
            @load="setLogoDimensions"
          />
        </a>
      </div>

      <div :class="b('menu-mobile-button', { active: isMainSubmenuActive })">
        <e-button-hamburger @click="toggleMobileMenu(mobileMenuType.Main)" />
      </div>

      <c-main-navigation
        :class="b('menu-desktop')"
        :navigation-items="mainNavigation"
      />

      <div :class="b('icon-menu')">
        <c-embedded-niko-search />
        <c-user-navigation
          v-if="!!sessionStore.myNikoPortal"
          @on-button-click="toggleMobileMenu(mobileMenuType.User)"
        />
      </div>

      <c-language-selector :class="b('language-selector-desktop')" />
    </nav>

    <c-main-navigation-mobile-wrapper :active="isMobileMenuActive">
      <template v-if="isMainSubmenuActive">
        <c-main-navigation
          :class="b('menu-mobile')"
          :navigation-items="mainNavigation"
          variant="mobile"
        />

        <c-language-selector :class="b('language-selector-mobile')" />
      </template>

      <template v-if="!!sessionStore.myNikoPortal && isUserSubmenuActive">
        <c-user-navigation-menu variant="mobile" />
      </template>
    </c-main-navigation-mobile-wrapper>
  </header>
</template>

<script lang="ts">
  import { Ref, defineComponent, ref } from 'vue';
  import useSessionStore from '@/stores/session';
  import cEmbeddedNikoSearch from '@/components/c-embedded-niko-search.vue';
  import cLanguageSelector from '@/components/c-language-selector.vue';
  import cMainNavigationMobileWrapper from '@/components/c-main-navigation-mobile-wrapper.vue';
  import cMainNavigation from '@/components/c-main-navigation.vue';
  import cUserNavigationMenu from '@/components/c-user-navigation-menu.vue';
  import cUserNavigation from '@/components/c-user-navigation.vue';
  import eButtonHamburger from '@/elements/e-button-hamburger.vue';

  enum MobileMenuType {
    Main = 'main',
    User = 'user',
  }

  type Setup = {
    mobileMenuType: typeof MobileMenuType;
    sessionStore: ReturnType<typeof useSessionStore>;
    logo: Ref<HTMLImageElement | undefined>;
  };

  type Data = {
    isMobileMenuActive: boolean;
    activeMobileMenu: MobileMenuType | null;
  };

  /**
   * Renders the page header.
   */
  export default defineComponent({
    name: 'c-header',
    components: {
      cUserNavigationMenu,
      cMainNavigationMobileWrapper,
      cUserNavigation,
      cLanguageSelector,
      eButtonHamburger,
      cMainNavigation,
      cEmbeddedNikoSearch,
    },

    // components: {},

    // props: {},
    setup(): Setup {
      return {
        mobileMenuType: MobileMenuType,
        sessionStore: useSessionStore(),
        logo: ref(),
      };
    },

    data(): Data {
      return {
        isMobileMenuActive: false,
        activeMobileMenu: null,
      };
    },

    computed: {
      mainNavigation() {
        return this.sessionStore.mainNavigation;
      },

      isMainSubmenuActive(): boolean {
        return this.isMobileMenuActive && this.activeMobileMenu === MobileMenuType.Main;
      },

      isUserSubmenuActive(): boolean {
        return this.isMobileMenuActive && this.activeMobileMenu === MobileMenuType.User;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Sets `width` and `height` of logo dynamically, since the dimensions are unknown considering
       * different logo dimensions for the different tenants.
       */
      setLogoDimensions(): void {
        const { naturalWidth, naturalHeight } = this.logo || {};

        if (this.logo && naturalWidth && naturalHeight) {
          this.logo.setAttribute('width', `${naturalWidth}`);
          this.logo.setAttribute('height', `${naturalHeight}`);
        }
      },

      toggleMobileMenu(selectedNav: MobileMenuType): void {
        if (!this.isMobileMenuActive || this.activeMobileMenu !== selectedNav) {
          this.isMobileMenuActive = true;
          this.activeMobileMenu = selectedNav;
        } else {
          this.isMobileMenuActive = false;
          this.activeMobileMenu = null;
        }
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-header {
    $this: &;
    $c-header-breakpoint: md;

    position: fixed;
    top: 0;
    display: flex;
    flex-direction: column;
    height: var(--theme-header-height);
    width: 100%;
    background-color: var(--theme-color-primary--1);
    box-shadow: 0 5px 10px rgba(var(--theme-color-grayscale--300-rgb), 0.2);
    z-index: map.get(variables.$va-z-index, 'navigation');

    @include mixins.media($down: sm) {
      &--is-mobile-menu-active {
        height: 100%;
        background-color: rgba(var(--theme-color-primary--1-rgb), 0.2);
      }
    }

    &__container {
      @include mixins.layout;

      display: flex;
      flex-direction: column;
      width: 100%;
      background: var(--theme-color-primary--1);
      flex-grow: inherit;
      box-shadow: 0 5px 10px rgba(var(--theme-color-grayscale--300-rgb), 0.3);
      z-index: 10;

      @include mixins.media($c-header-breakpoint) {
        height: 100%;
        flex-direction: row;
        align-items: center;
        box-shadow: none;
      }
    }

    &__brand-logo-container {
      display: flex;
      justify-content: flex-start;
      width: 9rem;
      height: var(--theme-header-height);
    }

    &__brand-link {
      display: flex;
      align-items: center;
      justify-content: start;
      width: 100%;
      height: 100%;
    }

    &__brand-logo {
      @include mixins.media($down: sm) {
        width: auto;
        height: 2.6rem;
      }
    }

    &__menu-desktop {
      display: none;
      height: var(--theme-header-height);
      width: 100%;
      justify-content: flex-end;

      @include mixins.media($c-header-breakpoint) {
        display: flex;
      }
    }

    &__icon-menu {
      margin-left: 3rem;
      height: var(--theme-header-height);
      display: flex;

      @include mixins.media($down: sm) {
        position: absolute;
        top: 0;
        right: var(--theme-header-height);
      }
    }

    &__menu-mobile-button {
      display: block;
      width: var(--theme-header-height);
      height: var(--theme-header-height);
      margin-bottom: auto;
      position: absolute;
      top: 0;
      right: 0;

      @include mixins.media($c-header-breakpoint) {
        display: none;
      }

      &:hover,
      &--active {
        background: var(--theme-color-grayscale--200);
      }
    }

    &__menu-mobile {
      display: flex;
      width: 100%;
      flex-direction: column;
      height: calc(100% - (var(--theme-header-height) - 90px));
      overflow: hidden;
      overflow-y: scroll;
      scrollbar-width: none;
    }

    &__language-selector-desktop {
      display: none;
      margin-right: 0;

      @include mixins.media($c-header-breakpoint) {
        display: block;
        margin-left: 1.5rem;
      }
    }

    &__language-selector-mobile {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 1.5rem 0;
    }
  }
</style>
